export interface ServiceFlatFeeDetail {
  planId: string;
  planName: string;
  price: number;
  priceEffectiveDate: string;
  assignmentStartDate: string;
  assignmentEndDate: string | null;
}

export interface PatchingTimeEntryDetail {
  workedDate: string;
  timeSpentMinutes: number;
  hours: number;
}

export interface PatchingMonthlyHours {
  month: string;
  totalMinutes: number;
  totalHours: number;
}

export interface PatchingResourceBreakdown {
  userId: string;
  userName: string;
  email: string;
  groupId: string;
  groupName: string;
  totalMinutes: number;
  totalHours: number;
  hourlyRate: number;
  rateSource: 'engagement_agent' | 'group_default' | 'fallback';
  billableAmount: number;
  monthlyHours?: PatchingMonthlyHours[];
  timeEntries: PatchingTimeEntryDetail[];
}

export interface PatchingBillingLine {
  patchingType: 'WINDOWS' | 'LINUX';
  patchingTypeLabel: string;
  billingCycle: 'MONTHLY' | 'QUARTERLY';
  billingCycleLabel: string;
  /** Configured amount (reference only — not added to totals). */
  billingAmount: number;
  loggedHours: number;
  /** Billable resource charges (hours × rate) for this line. */
  totalAmount: number;
  showInPeriod?: boolean;
  /** Quarterly cycle anchored to engagement start (YYYY-MM). */
  quarterCycleStartMonth?: string | null;
  quarterCycleEndMonth?: string | null;
  quarterCycleMonths?: string[];
  isQuarterlyBillingMonth?: boolean;
  resourceBreakdown: PatchingResourceBreakdown[];
}

export interface CustomerPatchingBillingLine extends PatchingBillingLine {
  engagementId: string;
  engagementNumber: string;
  engagementName: string;
}

export interface EngagementBilling {
  engagementId: string;
  engagementNumber: string;
  engagementName: string;
  customerId: string;
  customerName: string;
  billingPeriod: string;
  totalMinutes: number;
  totalHours: number;
  totalBillableAmount: number;
  minimumServiceFee: number;
  platformCost: number;
  serviceFlatFee: number;
  serviceFlatFeeDetails?: ServiceFlatFeeDetail[];
  appliedServiceFee: number;
  finalBill: number;
  currency: string;
  agentCount: number;
  isServiceCreditEngagement?: boolean;
  complimentaryHoursBudget?: number | null;
  complimentaryHoursAvailable?: number;
  complimentaryHoursLogged?: number;
  complimentaryHoursRemaining?: number;
  overageHours?: number;
  overageAmount?: number;
  cycleStartMonth?: string | null;
  cycleEndMonth?: string | null;
  patchingTotalAmount?: number;
  patchingReferenceAmount?: number;
  patchingServices?: PatchingBillingLine[];
  isPatchingEngagement?: boolean;
  patchingBillingCycle?: 'MONTHLY' | 'QUARTERLY';
  /** Month when quarterly patching bill is generated (e.g. "Jun 2025"). */
  patchingQuarterLabel?: string | null;
  isPatchingQuarterBillingMonth?: boolean;
  dashboardFinalBill?: number;
  isFixedHoursCustomer?: boolean;
  fixedHoursBreakdown?: EngagementFixedHoursBreakdown;
}

export interface AgentBillingBreakdown {
  userId: string;
  userName: string;
  email: string;
  groupId: string;
  groupName: string;
  hourlyRate: number;
  rateSource: 'engagement_agent' | 'group_default' | 'fallback';
  totalMinutes: number;
  totalHours: number;
  billableAmount: number;
}

export interface BillingSummary {
  totalEngagements: number;
  totalHours: number;
  totalBillableAmount: number;
  totalMinimumServiceFee: number;
  totalPlatformCost: number;
  totalServiceFlatFee: number;
  totalOverageAmount?: number;
  totalFinalBill: number;
}

export interface BillingSummaryResponse {
  billings: EngagementBilling[];
  total: number;
  summary: BillingSummary;
  complimentaryHours?: CustomerComplimentaryHoursInfo;
  fixedHours?: CustomerFixedHoursInfo;
}

export interface EngagementBillingDetail {
  engagement: {
    id: string;
    number: string;
    name: string;
    customerId: string;
    customerName: string;
    minimumServiceFee: number;
    platformCost: number;
    currency: string;
    isServiceCreditEngagement?: boolean;
    complimentaryHours?: number | null;
    isFixedHoursCustomer?: boolean;
  };
  billingPeriod: string;
  agentBreakdown: AgentBillingBreakdown[];
  serviceFlatFeeDetails: ServiceFlatFeeDetail[];
  fixedHoursBreakdown?: EngagementFixedHoursBreakdown;
  summary: {
    totalMinutes: number;
    totalHours: number;
    totalBillableAmount: number;
    minimumServiceFee: number;
    platformCost: number;
    serviceFlatFee: number;
    patchingTotalAmount?: number;
    overageHours?: number;
    overageAmount?: number;
    appliedServiceFee: number;
    finalBill: number;
    dashboardFinalBill?: number;
    isPatchingEngagement?: boolean;
  };
  patchingServices?: PatchingBillingLine[];
}

// Billing Workflow Types
export type BillingStatus = 'DRAFT' | 'APPROVED' | 'SENT_TO_FINANCE' | 'SENT_TO_CUSTOMER';

export interface BillingRecordUser {
  id: string;
  name: string;
  email: string;
}

export interface BillingRecord {
  id: string;
  engagementId: string;
  billingPeriod: string;
  status: BillingStatus;
  draftedAt: string;
  draftedBy: BillingRecordUser | null;
  approvedAt: string | null;
  approvedBy: BillingRecordUser | null;
  sentToFinanceAt: string | null;
  sentToFinanceBy: BillingRecordUser | null;
  sentToCustomerAt: string | null;
  sentToCustomerBy: BillingRecordUser | null;
  approvalNotes: string | null;
  financeNotes: string | null;
  customerNotes: string | null;
  totalBillableAmount: number | null;
  minimumServiceFee: number | null;
  platformCost: number | null;
  serviceFlatFee: number | null;
  finalBill: number | null;
  engagement?: {
    id: string;
    number: string;
    name: string;
  };
  isNew?: boolean;
}

// -------- Customer-wise Billing Types --------

export interface CustomerResourceCharge {
  userId: string;
  userName: string;
  email: string;
  groupId: string;
  groupName: string;
  hourlyRate: number;
  rateSource: 'engagement_agent' | 'group_default' | 'fallback';
  totalMinutes: number;
  totalHours: number;
  billableAmount: number;
  engagementId?: string;
  engagementNumber?: string;
  engagementName?: string;
  actualHours?: number;
  includedHours?: number;
  overageHours?: number;
}

export interface FixedHoursResourceLine {
  engagementId: string;
  engagementNumber: string;
  engagementName: string;
  userId: string;
  userName: string;
  email: string;
  groupId: string;
  groupName: string;
  hourlyRate: number;
  rateSource: 'engagement_agent' | 'group_default' | 'fallback';
  totalMinutes: number;
  actualHours: number;
  includedHours: number;
  overageHours: number;
  billableAmount: number;
}

export interface FixedHoursEngagementRow {
  engagementId: string;
  engagementNumber: string;
  engagementName: string;
  loggedHours: number;
  includedHours: number;
  overageHours: number;
  overageAmount: number;
}

export interface CustomerFixedHoursInfo {
  enabled: boolean;
  fixedHours: number;
  totalActualHours: number;
  totalIncludedHours: number;
  totalRemainingHours: number;
  totalOverageHours: number;
  totalOverageAmount: number;
  engagementRows: FixedHoursEngagementRow[];
  includedRows: FixedHoursResourceLine[];
  overageRows: FixedHoursResourceLine[];
  resourceLines: FixedHoursResourceLine[];
}

export interface EngagementFixedHoursBreakdown {
  enabled: boolean;
  showCalculation: boolean;
  customerFixedHours: number;
  totalActualHours: number;
  totalIncludedHours: number;
  totalOverageHours: number;
  totalOverageAmount: number;
  includedRows: FixedHoursResourceLine[];
  overageRows: FixedHoursResourceLine[];
  resourceLines: FixedHoursResourceLine[];
}

export interface CustomerBillingSummary {
  totalResourceHours: number;
  totalResourceCharges: number;
  totalPlatformFee: number;
  servicePlanFee: number;
  totalServiceFlatFee: number;
  totalOverageAmount: number;
  totalPatchingAmount: number;
  appliedServiceFee: number;
  finalBill: number;
}

/** Logged time on service-credit engagements, merged by user+group (not in resourceCharges). */
export interface ComplimentaryResourceRow {
  userId: string;
  userName: string;
  email: string;
  groupId: string;
  groupName: string;
  hourlyRate: number;
  rateSource: 'engagement_agent' | 'group_default' | 'fallback';
  totalMinutes: number;
  totalHours: number;
  billableAmount: number;
}

export interface CustomerComplimentaryHoursInfo {
  hasServiceCreditEngagements: boolean;
  engagementNames: string[];
  engagementRows: Array<{
    engagementId: string;
    engagementNumber: string;
    engagementName: string;
    cycleStartMonth: string | null;
    cycleEndMonth: string | null;
    configuredComplimentaryHours: number;
    availableHours: number;
    loggedHours: number;
    remainingHours: number;
    overageHours: number;
    overageAmount: number;
  }>;
  resourceRows: ComplimentaryResourceRow[];
  totalComplimentaryHours: number;
  totalConfiguredHours: number;
  totalAvailableHours: number;
  totalLoggedHours: number;
  totalRemainingHours: number;
  totalOverageHours: number;
  totalOverageAmount: number;
}

export interface CustomerBillingAggregate {
  customerId: string;
  customerName: string;
  billingPeriod: string;
  currency: string;
  resourceCharges: CustomerResourceCharge[];
  serviceFlatFeeDetails?: ServiceFlatFeeDetail[];
  patchingServices?: CustomerPatchingBillingLine[];
  summary: CustomerBillingSummary;
  complimentaryHours?: CustomerComplimentaryHoursInfo;
  fixedHours?: CustomerFixedHoursInfo;
}

export interface BillingRecordsResponse {
  records: BillingRecord[];
  total: number;
}

export interface UpdateBillingStatusRequest {
  status: BillingStatus;
  notes?: string;
  snapshotBillingData?: boolean;
}

// Status display configuration
export const BILLING_STATUS_CONFIG: Record<BillingStatus, {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  nextStatus: BillingStatus | null;
  nextStatusLabel: string | null;
  canRevert: boolean;
  revertStatus: BillingStatus | null;
}> = {
  DRAFT: {
    label: 'Draft',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-300',
    nextStatus: 'APPROVED',
    nextStatusLabel: 'Approve',
    canRevert: false,
    revertStatus: null
  },
  APPROVED: {
    label: 'Approved',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-300',
    nextStatus: 'SENT_TO_FINANCE',
    nextStatusLabel: 'Send to Finance',
    canRevert: true,
    revertStatus: 'DRAFT'
  },
  SENT_TO_FINANCE: {
    label: 'Sent to Finance',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    borderColor: 'border-yellow-300',
    nextStatus: 'SENT_TO_CUSTOMER',
    nextStatusLabel: 'Send to Customer',
    canRevert: true,
    revertStatus: 'APPROVED'
  },
  SENT_TO_CUSTOMER: {
    label: 'Sent to Customer',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-300',
    nextStatus: null,
    nextStatusLabel: null,
    canRevert: false,
    revertStatus: null
  }
};
